import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatErr(detail) {
  if (detail == null) return "Login failed. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success("Welcome back, champ.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#050505] spotlight px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="bg-brand text-black font-display text-3xl px-3 py-1 leading-none">CWF</span>
          <span className="font-display text-3xl tracking-wide">Admin</span>
        </Link>
        <form onSubmit={submit} className="bg-[#111] border border-zinc-800 p-10 space-y-6">
          <div>
            <h1 className="font-display text-4xl uppercase tracking-tight">Control Room</h1>
            <p className="text-zinc-500 mt-1">Sign in to manage the federation.</p>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Email</label>
            <div className="relative mt-2">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-zinc-800 text-white pl-10 pr-4 py-3 focus:outline-none focus:border-brand" placeholder="admin@cwf.com" />
            </div>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Password</label>
            <div className="relative mt-2">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-zinc-800 text-white pl-10 pr-4 py-3 focus:outline-none focus:border-brand" placeholder="••••••••" />
            </div>
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loading}
            className="w-full bg-brand hover:bg-brand-hi disabled:opacity-60 text-black font-display text-2xl uppercase tracking-wide py-4 transition-colors">
            {loading ? "Signing in..." : "Enter"}
          </button>
        </form>
        <Link to="/" className="block text-center text-zinc-600 hover:text-zinc-400 mt-6 font-mono text-sm">← Back to site</Link>
      </div>
    </div>
  );
}
